import {
  DraggableDrawer,
  LicenseDetail,
  CarouselMessage,
  Resize,
  FirmwareDownload
} from '@monorepo/shared/components';
import { useEffect, useState } from 'react';
import { Button, FlexboxGrid, Stack } from 'rsuite';
import {
  UPGRADE_STATE,
  useAppSelector,
  useGetSystemDataQuery
} from '@monorepo/shared';
import { HomeElms, InstanceAction, server } from '../../config/const';
import { selectInstances } from '@monorepo/shared/store/instanceSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';
import { selectAssets } from '@monorepo/shared/store/assetSlice';
import { selectInstancesExtraInfo } from '@monorepo/shared/store/instanceExtraSlice';
import { t } from 'i18next';

interface IProps {
  rowData: Record<string, any>;
}

const styleActionBtn: React.CSSProperties = {
  top: '2px',
  position: 'relative',
  marginRight: '5px'
};
const InstanceInfo = ({ rowData }: IProps) => {
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const assets = useAppSelector(selectAssets);
  const currentGroup = useAppSelector(selectCurrentGroup);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [asset, setAsset] = useState<Record<string, any>>({});
  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [extraInfo, setExtraInfo] = useState<Record<string, any>>({});
  const [upgradeInfo, setUpgradeInfo] = useState<Record<string, any>>({});

  const { data: systemData, isSuccess: dataSuccess } = useGetSystemDataQuery({
    server: server
  });

  useEffect(() => {
    const data = assets?.[currentGroup.id]?.[rowData?.account_id];
    if (data) {
      setAsset({ ...data });
    }
  }, [assets]);
  useEffect(() => {
    const data = instances?.[currentGroup.id]?.[rowData?.account_id];
    const extra = instancesExtraInfo?.[currentGroup.id]?.[rowData?.account_id];
    if (data) {
      setInstance({ ...data });
      setIsExpired(data?.is_expired);
    }
    if (extra) {
      setExtraInfo(extra);
      setIsActive(extra?.is_active);
      if (extra?.is_active && extra?.firmware_download) {
        setUpgradeInfo({
          ...extra.firmware_download,
          state: UPGRADE_STATE.NEED_UPGRADE
        });
      }
    }
  }, [instances, currentGroup, instancesExtraInfo]);

  return (
    <Stack className="instance-info" direction="column" alignItems="stretch">
      <Stack direction="column" alignItems="stretch" spacing={10}>
        <Stack justifyContent="space-between">
          <div>
            <b style={{ fontSize: '16px' }}>
              {t('common.expandedRow.information')}
            </b>
            {!isExpired && isActive && (
              <Resize
                rowData={rowData}
                action={InstanceAction.resize}
                extraInfo={extraInfo}
              />
            )}
          </div>
        </Stack>
        <Stack.Item style={{ width: '70%' }}>
          <CarouselMessage
            license={instance?.detail?.license}
            systemData={systemData}
            upgradeInfo={upgradeInfo}
            userType={rowData?.user_type}
          />
        </Stack.Item>
        <FlexboxGrid>
          <FlexboxGrid.Item colspan={21}>
            <Stack direction={'column'} alignItems="flex-start" spacing={8}>
              <Stack spacing={16}>
                <div className="instance-detail-title">
                  {t('common.expandedRow.serviceDescription')}
                </div>
                <div>
                  <p>{`${HomeElms?.name} Cloud`}</p>
                </div>
              </Stack>
              <Stack spacing={16}>
                <div className="instance-detail-title">
                  {t('common.expandedRow.expirationDate')}
                </div>
                <Stack alignItems="center" spacing={10}>
                  <b style={{ lineHeight: '21px' }}>{instance?.expiry_date}</b>
                  {asset?.asset && (
                    <Button
                      appearance="link"
                      onClick={() => setOpenDetail(true)}
                      style={{ padding: 0 }}
                    >
                      {t('common.expandedRow.detail')}
                    </Button>
                  )}
                  <DraggableDrawer
                    className="api-portal-drawer"
                    open={openDetail}
                    setOpen={setOpenDetail}
                    size="lg"
                    title={<b>{t('common.expandedRow.licenseDetail')}</b>}
                  >
                    <LicenseDetail
                      data={asset?.asset}
                      systemData={systemData}
                    />
                  </DraggableDrawer>
                </Stack>
              </Stack>
              <Stack spacing={16}>
                <div className="instance-detail-title">
                  {t('common.expandedRow.serviceVersion')}
                </div>
                <Stack spacing={8} style={{ height: '21px' }}>
                  <b>{instance?.firmware}</b>
                  {!isExpired && isActive && (
                    <FirmwareDownload rowData={rowData} />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Stack>
    </Stack>
  );
};

export default InstanceInfo;
